// Generic widget chrome + the seven states every widget renders (spec §10.4):
// loading, error, empty, partial-coverage, pre-boundary, stale, live.
// Also wires the widget's declared realtime channels (refetch / append / patch) and the polling
// fallback when the WebSocket is down. Data is only requested once the widget is near the
// viewport, so long dashboards load fast on phones.
import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useWidgetActionMutation, useWidgetDataQuery, useSavePrefsMutation, errorMessage } from '../app/api';
import type { Shell, WidgetSchema } from '../app/types';
import { REGISTRY } from '../registry';
import { validateWidgetProps } from '../registry/schemas';
import type { RealtimeEvent } from '../realtime/client';
import { reloadsOn } from '../realtime/reload';
import { rememberedHeight, rememberHeight } from './heights';
import { SourceIcons } from '../ui/SourceIcons';
import { useChannels, useConnectionStatus, useDebounced } from '../realtime/hooks';
import { FreshnessIndicator } from '../ui/Badges';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toasts';
import { TableView } from '../registry/widgets/DataTable';

function expandChannels(channels: string[], filters: Record<string, unknown>, shell: Shell): string[] {
  const out: string[] = [];
  const storeIds = (v: unknown) => (v === 'all' || v === undefined || (Array.isArray(v) && v.length === 0) ? shell.stores.map((s) => s.id) : Array.isArray(v) ? v.map(String) : [String(v)]);
  for (const ch of channels) {
    if (ch.includes('$stores')) storeIds(filters.stores).forEach((id) => out.push(ch.replace('$stores', id)));
    else if (ch.includes('$store')) storeIds(filters.store).forEach((id) => out.push(ch.replace('$store', id)));
    else out.push(ch.replace('$tenant', shell.tenant.id));
  }
  return [...new Set(out)];
}

/** Widget types whose content needs a definite box (charts measure it; feeds and chat scroll inside). */
const FIXED_HEIGHT = new Set(['line-chart', 'bar-chart', 'heatmap', 'live-feed', 'ai-assistant', 'alert-list', 'funnel']);

/** True once the element has come within `margin` of the viewport (then stays true). */
function useNearViewport<T extends Element>(margin = '600px 0px') {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || !ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        io.disconnect();
      }
    }, { rootMargin: margin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [near, margin]);
  return [ref, near] as const;
}

interface MenuItem {
  label: string;
  icon: string;
  onClick: () => void;
}

function WidgetMenu({ items, title }: { items: MenuItem[]; title: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div className={`w-tools popover-anchor${open ? ' open' : ''}`} ref={ref}>
      <button className="icon-btn" aria-label={`${title} options`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="more" size={16} />
      </button>
      {open && (
        <div className="popover right" role="menu" style={{ minWidth: 220 }}>
          {items.map((it) => (
            <button
              key={it.label}
              className="option"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              <Icon name={it.icon} size={14} />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CalculationDialog({ citation, title, onClose }: { citation: any; title: string; onClose: () => void }) {
  return (
    <Modal title={`How “${title}” is calculated`} onClose={onClose}>
      {(citation.metrics ?? []).map((m: any) => (
        <div key={m.id}>
          <div style={{ fontWeight: 600 }}>{m.label}</div>
          <div className="secondary">{m.formula}</div>
        </div>
      ))}
      {citation.sources?.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 12 }}>Data source</div>
          {citation.sources.join(' · ')}
        </div>
      )}
      {citation.hmrc?.months?.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 12 }}>Currency conversion</div>
          HMRC monthly rates for {citation.hmrc.months.join(', ')} (amount ÷ units per £1)
        </div>
      )}
      {citation.query && (
        <div>
          <div className="muted" style={{ fontSize: 12 }}>Reproducible query</div>
          <code className="mono" style={{ overflowWrap: 'anywhere' }}>{citation.query}</code>
        </div>
      )}
    </Modal>
  );
}

export function WidgetFrame({ widget, dashboardId, filters, shell, index = 0 }: { widget: WidgetSchema; dashboardId: string; filters: Record<string, unknown>; shell: Shell; index?: number }) {
  const toast = useToast();
  const entry = REGISTRY[widget.type as keyof typeof REGISTRY];
  const validation = useMemo(() => validateWidgetProps(widget.type, widget.props), [widget.type, widget.props]);
  const conn = useConnectionStatus();
  const [ref, near] = useNearViewport<HTMLElement>();
  const hasRealtime = !!widget.realtime?.channels?.length;
  const needsData = !!widget.dataSource;
  const active = needsData && !!entry && near;
  const query = useWidgetDataQuery(
    { dashboardId, widgetId: widget.id, filters },
    { skip: !active, pollingInterval: active && hasRealtime && conn !== 'open' ? (shell.realtime?.fallbackPollSeconds ?? 30) * 1000 : 0 },
  );
  const [runAction] = useWidgetActionMutation();
  const [savePrefs] = useSavePrefsMutation();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [explain, setExplain] = useState(false);
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  // A refetch requested while one is in flight would be merged into it and could return data
  // from before the triggering change, so it is queued and replayed once the current one ends.
  const again = useRef(false);
  const refetch = useDebounced(() => {
    if (!active) return;
    if (query.isFetching) again.current = true;
    else query.refetch();
  }, shell.realtime?.debounceMs ?? 500);
  useEffect(() => {
    if (active && !query.isFetching && again.current) {
      again.current = false;
      query.refetch();
    }
  }, [active, query.isFetching, query]);

  const channels = useMemo(() => (widget.realtime ? expandChannels(widget.realtime.channels, filters, shell) : []), [widget.realtime, filters, shell]);
  useChannels(channels, (e) => {
    const action = widget.realtime?.action;
    if (action === 'refetch' && reloadsOn(e, widget.realtime?.lifecycle)) refetch();
    else if (action === 'append' && (!widget.realtime?.event || e.event === widget.realtime.event)) setEvents((xs) => [e, ...xs].slice(0, 60));
    else if (action === 'patch') setEvents((xs) => [...xs, e].slice(-200));
  });

  const loading = needsData && (!near || query.isLoading || (query.isUninitialized && active));
  // Content-sized widgets reserve the height they had last time while they load.
  const sized = !FIXED_HEIGHT.has(widget.type);
  const reserve = loading && sized ? rememberedHeight(dashboardId, widget.id) : null;
  const loaded = needsData && !!query.data?.data && !loading;
  useEffect(() => {
    if (!loaded || !sized) return;
    const frame = requestAnimationFrame(() => {
      // offsetHeight is in the page's own pixels (unaffected by the big-screen zoom).
      const h = (ref.current as HTMLElement | null)?.offsetHeight;
      if (h) rememberHeight(dashboardId, widget.id, h);
    });
    return () => cancelAnimationFrame(frame);
  }, [loaded, sized, query.data, dashboardId, widget.id, ref]);

  if (!entry || !validation.ok) {
    return (
      <Frame ref={ref} title={widget.title} widget={widget} index={index}>
        <div className="state">
          <strong>Invalid widget configuration</strong>
          {!entry ? `No registered widget type "${widget.type}".` : validation.ok ? '' : validation.error}
        </div>
      </Frame>
    );
  }

  const data = query.data?.data;
  const coverage = data?.coverage;
  const table = view === 'table' && entry.toTable && data ? entry.toTable(data, widget) : null;
  const C = entry.component;
  const ctx = {
    shell,
    dashboardId,
    filters,
    events,
    refetch: () => {
      if (active) query.refetch();
    },
    action: (action: string, rowId?: string | number | null, input?: unknown) => runAction({ dashboardId, widgetId: widget.id, filters, action, rowId, input }).unwrap(),
  };

  let body: ReactNode;
  if (loading) {
    // Placeholders take the shape of what arrives (a figure and its comparison line; a row of
    // cards), so nothing below moves when the data lands.
    body =
      widget.type === 'metric-card' ? (
        <div className="kpi-skeleton" aria-label="Loading">
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      ) : (
        <div className="skeleton" style={{ height: '100%', minHeight: (widget.props as any)?.view === 'cards' ? 248 : 32 }} aria-label="Loading" />
      );
  } else if (needsData && query.isError && !data) {
    body = (
      <div className="state">
        <strong>Couldn’t load this widget</strong>
        <span>{errorMessage(query.error)}</span>
        <div style={{ marginTop: 8 }}>
          <button className="btn sm" onClick={() => query.refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  } else if (coverage?.state === 'pre-boundary' && (data?.value === null || data?.value === undefined)) {
    const es = widget.coverageRules?.emptyState;
    body = (
      <div className="state">
        <strong>{es?.title ?? 'No data for this period'}</strong>
        {coverage.note ?? es?.message}
      </div>
    );
  } else if (coverage?.state === 'none') {
    body = (
      <div className="state">
        <strong>No data source</strong>
        {coverage.note}
      </div>
    );
  } else if (needsData && entry.isEmpty?.(data)) {
    body = (
      <div className="state">
        <strong>No data</strong>
        {data?.currencyNote ?? 'Nothing matches the current filters.'}
      </div>
    );
  } else if (table) {
    body = <TableView model={table} title={widget.title} />;
  } else {
    body = <C widget={widget} props={validation.props} data={data} ctx={ctx} />;
  }

  const items: MenuItem[] = [];
  if (entry.toTable && data) items.push({ label: view === 'table' ? 'Show as chart' : 'Show as table', icon: view === 'table' ? 'chart' : 'table', onClick: () => setView((v) => (v === 'table' ? 'chart' : 'table')) });
  if (data?.citation?.metrics?.length > 0) items.push({ label: 'How is this calculated?', icon: 'info', onClick: () => setExplain(true) });
  items.push({
    label: 'Hide this widget',
    icon: 'eyeOff',
    onClick: async () => {
      try {
        await savePrefs({ dashboardId, hideWidget: widget.id }).unwrap();
        toast(`“${widget.title}” hidden — show it again from the bar at the top of this page`);
      } catch (e) {
        toast(errorMessage(e), 'error');
      }
    },
  });

  // Notes the widget body already shows as its empty state are not repeated above it.
  const emptyShown = needsData && !!data && entry.isEmpty?.(data);
  const note = coverage?.state === 'partial' ? coverage.note : emptyShown ? null : data?.currencyNote ?? (data?.unsupported?.length && widget.type !== 'metric-card' ? 'Some metrics are not available for these filters.' : null);

  return (
    <Frame
      ref={ref}
      title={widget.title}
      widget={widget}
      index={index}
      reserve={reserve}
      status={
        <>
          {note && widget.type === 'metric-card' && (
            <span className="w-info" tabIndex={0} role="note" aria-label={note} title={note}>
              <Icon name="info" size={14} />
            </span>
          )}
          {needsData && query.isError && data && (
            <button
              className="badge critical update-failed"
              onClick={() => query.refetch()}
              title={`The last update failed (${errorMessage(query.error)}). Showing the data loaded before. Click to try again.`}
            >
              <Icon name="alertCircle" size={12} />
              Update failed
            </button>
          )}
          <FreshnessIndicator freshness={data?.freshness} compact={widget.type === 'metric-card'} />
        </>
      }
      tools={shell.readOnly ? undefined : <WidgetMenu items={items} title={widget.title} />}
      note={widget.type === 'metric-card' ? null : note}
      refetching={query.isFetching && !query.isLoading}
      scroll={entry.scroll}
    >
      {body}
      {explain && data?.citation && <CalculationDialog citation={data.citation} title={widget.title} onClose={() => setExplain(false)} />}
    </Frame>
  );
}

const W8 = [2, 2, 2, 4, 4, 4, 4, 8, 8, 8, 8, 8, 8];

const Frame = forwardRef<HTMLElement, { title: string; widget: WidgetSchema; tools?: ReactNode; status?: ReactNode; children: ReactNode; note?: string | null; refetching?: boolean; scroll?: boolean; index?: number; reserve?: number | null }>(
  function Frame({ title, widget, tools, status, children, note, refetching, scroll, index = 0, reserve }, ref) {
    const w = Math.min(12, widget.layout.w);
    // Column spans per breakpoint (12 → 8 → 4 → phone), chosen so common rows still pack edge to edge.
    // On tablets KPI cards up to a third wide pair up rather than filling the row alone; on
    // mid-size screens (8 columns) tables half the width or wider take the full row, so their
    // numbers stay readable.
    const style = {
      ['--w12' as any]: w,
      ['--w8' as any]: widget.type === 'data-table' && w >= 6 ? 8 : W8[w],
      ['--w4' as any]: w <= 3 || (widget.type === 'metric-card' && w <= 4) ? 2 : 4,
      ['--h' as any]: widget.layout.h,
      ['--i' as any]: index, // entrance stagger
      ...(reserve ? { minHeight: reserve } : {}),
    };
    return (
      <section ref={ref} className="widget" style={style} aria-label={title} data-widget={widget.id} data-type={widget.type} data-fixed={FIXED_HEIGHT.has(widget.type) ? '' : undefined}>
        <header className="w-head">
          <div className="w-titles">
            <h3 className="w-title" title={widget.subtitle}>{title}</h3>
            {widget.subtitle && <div className="w-sub">{widget.subtitle}</div>}
          </div>
          {status && <div className="w-status">{status}</div>}
          <SourceIcons sources={widget.sources} />
          {tools}
        </header>
        {note && (
          <div className="w-note" role="note" title={note}>
            <Icon name="info" size={14} />
            <span>{note}</span>
          </div>
        )}
        <div className={`w-body${scroll ? ' scroll' : ''}${refetching ? ' refetching' : ''}`}>{children}</div>
      </section>
    );
  },
);
